import { useMemo } from "react";
import type { TaxonomyUrn } from "@shared/types/taxonomy.ts";
import { useTaxonomy } from "../../../../../context/TaxonomyProvider";
import type { TaxonomyView } from "../../../../../lib/taxonomyView";
import type {
  BatchJob,
  DocumentIndexingRefreshItemResult,
  DocumentIndexingRefreshSummary,
} from "../../../../../lib/corpusApi";
import { narrowByOutcome, type BatchJobItemRow } from "../../panel/batch_job_item_rows";
import {
  useBatchJobItemList,
  type UseBatchJobItemListResult,
} from "../../panel/useBatchJobItemList";
import { useBatchJobTabs, type UseBatchJobTabsResult } from "../../panel/useBatchJobTabs";
import {
  DOCUMENT_INDEXING_REFRESH_TABS,
  type DocumentIndexingRefreshTabConfig,
  type DocumentIndexingRefreshTabKey,
} from "./document_indexing_refresh_tabs";

/** One list row: the raw item plus its document_indexing_refresh result. A failed item has none. */
export type DocumentIndexingRefreshRow = BatchJobItemRow<DocumentIndexingRefreshItemResult>;

/** A field a refresh gave documents, with how many documents gained it. */
export interface DocumentIndexingRefreshGainedField {
  urn: TaxonomyUrn;
  documents: number;
}

/** What the panel header shows, read from the job's stored summary. */
export interface DocumentIndexingRefreshPanelSummary {
  fieldsAddedCount: number;
  noNewFieldsCount: number;
  /** Documents with no field tags after the run. */
  withoutFields: number;
  subjectsAdded: number;
  /** Fields gained, most documents first. */
  fieldsGained: DocumentIndexingRefreshGainedField[];
}

export interface UseDocumentIndexingRefreshPanelResult {
  /** Null while the job has not finished, as its summary is only written at the end. */
  summary: DocumentIndexingRefreshPanelSummary | null;
  tabs: UseBatchJobTabsResult<DocumentIndexingRefreshTabKey, DocumentIndexingRefreshTabConfig>;
  list: UseBatchJobItemListResult<DocumentIndexingRefreshItemResult>;
}

const narrowDocumentIndexingRefreshResult = narrowByOutcome<DocumentIndexingRefreshItemResult>(
  new Set(["fields_added", "no_new_fields"])
);

/** Reads a job's stored summary into what the panel header shows; null when there is none yet. */
export function toPanelSummary(
  stored: Record<string, unknown> | null,
  taxonomy: TaxonomyView
): DocumentIndexingRefreshPanelSummary | null {
  if (!stored) return null;
  const summary = stored as unknown as DocumentIndexingRefreshSummary;
  const fieldsGained = Object.keys(summary.fieldsGained)
    .filter((urn) => taxonomy.find(urn) !== null)
    .map((urn) => ({ urn, documents: summary.fieldsGained[urn] }))
    .sort((a, b) => b.documents - a.documents || a.urn.localeCompare(b.urn));
  return {
    fieldsAddedCount: summary.byOutcome.fields_added ?? 0,
    noNewFieldsCount: summary.byOutcome.no_new_fields ?? 0,
    withoutFields: summary.withoutFields,
    subjectsAdded: summary.subjectsAdded,
    fieldsGained,
  };
}

/** Drives the document_indexing_refresh batch job panel: the job's summary, and one paginated list per
 *  tab. Read-only — a refresh has no review, so nothing here changes a document. */
export function useDocumentIndexingRefreshPanel(
  job: BatchJob
): UseDocumentIndexingRefreshPanelResult {
  const tabs = useBatchJobTabs(DOCUMENT_INDEXING_REFRESH_TABS, "fields_added");
  const list = useBatchJobItemList({
    job,
    filters: tabs.tab.apiFilters,
    resetKey: tabs.activeTab,
    narrow: narrowDocumentIndexingRefreshResult,
  });
  const taxonomy = useTaxonomy();
  const summary = useMemo(() => toPanelSummary(job.summary, taxonomy), [job.summary, taxonomy]);

  return { summary, tabs, list };
}
